import { useState } from 'react'
import DataTable from '../../components/DataTable'
import StatsCard from '../../components/StatsCard'
import { mockReferrals } from '../../data/mockData'
import { TrendingUp, Share2, Award, DollarSign } from 'lucide-react'

export default function ReferralsPage() {
  const [sortKey, setSortKey] = useState('earnings')
  const [sortOrder, setSortOrder] = useState('desc')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortOrder('asc')
    }
  }

  const totalEarnings = mockReferrals.reduce((sum, r) => sum + r.earnings, 0)
  const totalReferrals = mockReferrals.reduce((sum, r) => sum + r.referralCount, 0)
  const avgConversionRate = (mockReferrals.reduce((sum, r) => sum + r.conversionRate, 0) / mockReferrals.length).toFixed(1)

  const columns = [
    { key: 'userName', label: 'User', sortable: true },
    { key: 'code', label: 'Referral Code', sortable: false, render: (v) => <code className="bg-pawssible-slate-100 px-2 py-1 rounded text-xs font-mono">{v}</code> },
    { key: 'referralCount', label: 'Referrals', sortable: true },
    { key: 'conversionRate', label: 'Conversion %', sortable: true, render: (v) => `${v}%` },
    {
      key: 'tier',
      label: 'Tier',
      render: (value) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${
          value === 'Gold' ? 'bg-yellow-100 text-yellow-700' :
          value === 'Silver' ? 'bg-gray-100 text-gray-700' :
          'bg-orange-100 text-orange-700'
        }`}>{value}</span>
      ),
    },
    { key: 'earnings', label: 'Earnings (AED)', sortable: true, render: (v) => `AED ${v.toLocaleString()}` },
  ]

  // Tier breakdown
  const tierStats = [
    { tier: 'Gold', count: mockReferrals.filter((r) => r.tier === 'Gold').length, commission: 20 },
    { tier: 'Silver', count: mockReferrals.filter((r) => r.tier === 'Silver').length, commission: 15 },
    { tier: 'Bronze', count: mockReferrals.filter((r) => r.tier === 'Bronze').length, commission: 10 },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-pawssible-slate-900">Referral Program</h1>
        <p className="text-pawssible-slate-600 mt-1">Pack Builder referral tracking & commission management</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatsCard
          label="Total Referrals"
          value={totalReferrals}
          icon={Share2}
          color="teal"
        />
        <StatsCard
          label="Active Referrers"
          value={mockReferrals.length}
          icon={Award}
          color="amber"
        />
        <StatsCard
          label="Avg Conversion Rate"
          value={`${avgConversionRate}%`}
          icon={TrendingUp}
          color="teal"
        />
        <StatsCard
          label="Total Payouts"
          value={`AED ${totalEarnings.toLocaleString()}`}
          icon={DollarSign}
          color="amber"
        />
      </div>

      {/* Tier Information */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Pack Builder Tier Structure</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tierStats.map((stat, i) => (
            <div key={i} className="border border-pawssible-slate-200 rounded-lg p-4">
              <p className="font-semibold text-pawssible-slate-900">{stat.tier}</p>
              <div className="mt-2 space-y-1">
                <div className="flex justify-between text-sm">
                  <span className="text-pawssible-slate-600">Active Members</span>
                  <span className="font-semibold text-pawssible-slate-900">{stat.count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-pawssible-slate-600">Commission Rate</span>
                  <span className="font-semibold text-pawssible-slate-900">{stat.commission}%</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4 flex-wrap">
        <button className="btn-primary">Process Payouts</button>
        <button className="btn-secondary">Generate Report</button>
        <button className="btn-secondary">View Fraud Alerts</button>
      </div>

      {/* Referrers Table */}
      <div>
        <h2 className="text-xl font-semibold text-pawssible-slate-900 mb-4">Top Referrers</h2>
        <DataTable
          columns={columns}
          data={mockReferrals}
          onSort={handleSort}
          sortKey={sortKey}
          sortOrder={sortOrder}
          pageSize={15}
        />
      </div>

      {/* Payout Schedule */}
      <div className="card">
        <h3 className="text-lg font-semibold text-pawssible-slate-900 mb-4">Next Payout Schedule</h3>
        <div className="space-y-3">
          <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
            <span className="text-pawssible-slate-600">Payout Date</span>
            <span className="font-semibold">April 30, 2024</span>
          </div>
          <div className="flex justify-between items-center border-b border-pawssible-slate-100 pb-3">
            <span className="text-pawssible-slate-600">Eligible Referrers</span>
            <span className="font-semibold">3</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-pawssible-slate-600">Total Amount (AED)</span>
            <span className="font-semibold text-lg text-pawssible-teal-600">{totalEarnings.toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  )
}
